import React, { useState, useMemo, useEffect, useRef } from "react";
import {
    Shell, Toolbar, TitleRow, TableTitle, CountChip,
    ToolbarRight, SearchBox, AddButton, TableWrap,
    StyledTable, Th, Tr, Td, CellText, CellInput,
    CellSelect, ActionsCell, ActionBtn, Footer,
    PgBtns, PgBtn, EmptyState, Badge,
} from "./Datatable.style";

const STATUS_STYLES = {
    active: { bg: "#dcfce7", color: "#166534" },
    inactive: { bg: "#fee2e2", color: "#991b1b" },
    pending: { bg: "#fef9c3", color: "#854d0e" },
};

const StatusBadge = ({ value }) => {
    const s = STATUS_STYLES[value?.toLowerCase()] ?? { bg: "#e0f2fe", color: "#3f88a5" };
    return <Badge $bg={s.bg} $color={s.color}>{value}</Badge>;
};

const Cell = ({ col, row, value, editing, index, onChange }) => {
    if (!editing) {
        if (col.type === "badge") return <StatusBadge value={value} />;
        if (col.renderCell) return col.renderCell(value, row, index);
        return <CellText title={String(value ?? "")}>{value ?? "—"}</CellText>;
    }

    if (col.type === "select") {
        return (
            <CellSelect value={value ?? ""} onChange={e => onChange(col.key, e.target.value)}>
                {(col.options || []).map(o => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </CellSelect>
        );
    }

    return (
        <CellInput
            value={value ?? ""}
            placeholder={col.placeholder ?? col.label ?? ""}
            autoFocus={!!col._autoFocus}
            onChange={e => onChange(col.key, e.target.value)}
        />
    );
};

const DataTable = ({
    title = "Table",
    columns: initialColumns = [],
    data = [],
    onSave,
    onDelete,
    onView,
    onAddRow,
    extraActions,
    searchable = true,
    searchFields,
    pageSize = 20,
    emptyMessage = "No records found",
    showPagination = true,

    serverSide = false,
    totalRecords = 0,
    currentPage = 1,
    onPageChange,
    onSearch,
    onSort,
}) => {
    const [rows, setRows] = useState(data);
    const [columns, setColumns] = useState(initialColumns);
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState(null);
    const [sortAsc, setSortAsc] = useState(true);
    const [page, setPage] = useState(1);

    const resizingColumn = useRef(null);

    useEffect(() => {
        setRows(data);
        setEditingId(null);
        setEditValues({});
    }, [data]);

    useEffect(() => {
        setColumns(initialColumns);
    }, [initialColumns]);

    const hasActions = !!(onView || onDelete || extraActions || onSave);

    const searchOn = searchFields ?? columns
        .filter(c => c.type !== "badge")
        .map(c => c.key);

    const processedRows = useMemo(() => {
        if (serverSide) {
            return data;
        }

        let r = [...rows];

        if (search) {
            const q = search.toLowerCase();

            r = r.filter(row =>
                searchOn.some(k =>
                    String(row[k] ?? "")
                        .toLowerCase()
                        .includes(q)
                )
            );
        }

        if (sortKey) {
            r.sort((a, b) => {
                const av = a[sortKey];
                const bv = b[sortKey];

                if (typeof av === "number" && typeof bv === "number") {
                    return sortAsc ? av - bv : bv - av;
                }

                if (av instanceof Date && bv instanceof Date) {
                    return sortAsc
                        ? av.getTime() - bv.getTime()
                        : bv.getTime() - av.getTime();
                }

                return sortAsc
                    ? String(av ?? "").localeCompare(String(bv ?? ""))
                    : String(bv ?? "").localeCompare(String(av ?? ""));
            });
        }

        return r;
    }, [
        serverSide,
        data,
        rows,
        search,
        searchOn,
        sortKey,
        sortAsc,
    ]);

    const totalPages = serverSide
        ? Math.max(1, Math.ceil(totalRecords / pageSize))
        : Math.max(1, Math.ceil(processedRows.length / pageSize));

    const pageRows = serverSide
        ? processedRows
        : processedRows.slice(
            (page - 1) * pageSize,
            page * pageSize
        );

    // Resizing Logic
    const startResizing = (e, key) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const colIndex = columns.findIndex(c => c.key === key);

        const startWidth = e.target.parentElement.offsetWidth;

        const onMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const newWidth = Math.max(50, startWidth + deltaX);

            setColumns(cols => cols.map((c, i) =>
                i === colIndex
                    ? { ...c, width: `${newWidth}px` }
                    : c
            ));
        };

        const onMouseUp = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const handleCellChange = (key, value) => {
        setEditValues(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSort = (key) => {
        let asc = true;

        if (sortKey === key) {
            asc = !sortAsc;
            setSortAsc(asc);
        } else {
            setSortKey(key);
            setSortAsc(true);
        }

        if (serverSide) {
            onSort?.({
                key,
                direction: asc ? "asc" : "desc",
            });
        }
    };

    const saveRow = (row) => {
        const updated = {
            ...row,
            ...editValues,
            _new: false,
        };

        setRows(prev =>
            prev.map(r =>
                r._id === row._id
                    ? updated
                    : r
            )
        );

        setEditingId(null);
        setEditValues({});

        onSave?.(updated);
    };

    const cancelEdit = (row) => {
        if (row._new) {
            setRows(prev =>
                prev.filter(r => r._id !== row._id)
            );
        }

        setEditingId(null);
        setEditValues({});
    };

    const handleDelete = (row) => {
        setRows(prev =>
            prev.filter(r => r._id !== row._id)
        );

        onDelete?.(row);
    };

    const addRow = () => {
        const blank = onAddRow?.() ?? {};

        const newRow = {
            ...blank,
            _id: `new_${Date.now()}`,
            _new: true,
        };

        setRows(prev => [newRow, ...prev]);

        setEditingId(newRow._id);
        setEditValues({ ...newRow });

        if (serverSide) {
            onPageChange?.(1);
        } else {
            setPage(1);
        }
    };

    const renderPaginationButtons = () => {
        const maxVisiblePages = 5;

        let startPage = Math.max(
            1,
            (serverSide ? currentPage : page) - Math.floor(maxVisiblePages / 2)
        );

        let endPage = startPage + maxVisiblePages - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return Array.from(
            { length: endPage - startPage + 1 },
            (_, i) => startPage + i
        ).map(n => (
            <PgBtn
                key={n}
                $active={(serverSide ? currentPage : page) === n}
                onClick={() => {
                    if (serverSide) {
                        onPageChange?.(n);
                    } else {
                        setPage(n);
                    }
                }}
            >
                {n}
            </PgBtn>
        ));
    };

    const activePage = serverSide ? currentPage : page;

    return (
        <Shell>
            <Toolbar>
                <TitleRow>
                    <TableTitle>{title}</TableTitle>
                    {/* <CountChip>{processedRows.length}</CountChip> */}
                </TitleRow>

                <ToolbarRight>
                    {searchable && (
                        <SearchBox>
                            <i className="fas fa-search" aria-hidden="true" />

                            <input
                                type="text"
                                placeholder="Search…"
                                value={search}
                                onChange={e => {
                                    const value = e.target.value;

                                    setSearch(value);

                                    if (serverSide) {
                                        onSearch?.(value);
                                    } else {
                                        setPage(1);
                                    }
                                }}
                            />
                        </SearchBox>
                    )}

                    {onAddRow && (
                        <AddButton onClick={addRow}>
                            <i className="fas fa-plus" aria-hidden="true" />
                            {" "}Add row
                        </AddButton>
                    )}
                </ToolbarRight>
            </Toolbar>

            <TableWrap>
                <StyledTable style={{ tableLayout: "fixed", width: "100%" }}>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <Th
                                    key={col.key}
                                    style={{
                                        width: col.width,
                                        position: "relative",
                                    }}
                                    $sortable={col.sortable}
                                    onClick={() =>
                                        col.sortable && handleSort(col.key)
                                    }
                                >
                                    <div
                                        style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {col.label}
                                    </div>

                                    {col.sortable && (
                                        <i
                                            className={
                                                sortKey === col.key
                                                    ? (
                                                        sortAsc
                                                            ? "fas fa-sort-up"
                                                            : "fas fa-sort-down"
                                                    )
                                                    : "fas fa-sort"
                                            }
                                        />
                                    )}

                                    <div
                                        onMouseDown={(e) => startResizing(e, col.key)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            position: "absolute",
                                            right: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: "8px",
                                            cursor: "col-resize",
                                            zIndex: 10,
                                        }}
                                    />
                                </Th>
                            ))}

                            {hasActions && (
                                <Th $right style={{ width: 110 }}>
                                    Actions
                                </Th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {pageRows.map((row, idx) => {
                            const isEditing = editingId === row._id;

                            const displayRow = isEditing
                                ? editValues
                                : row;

                            return (
                                <Tr
                                    key={row._id}
                                    $editing={isEditing}
                                    $new={row._new}
                                >
                                    {columns.map(col => (
                                        <Td key={col.key}>
                                            <Cell
                                                col={col}
                                                row={row}
                                                value={displayRow[col.key]}
                                                editing={
                                                    isEditing &&
                                                    col.editable !== false
                                                }
                                                index={
                                                    (activePage - 1) * pageSize + idx
                                                }
                                                onChange={handleCellChange}
                                            />
                                        </Td>
                                    ))}

                                    {hasActions && (
                                        <Td $right>
                                            <ActionsCell>
                                                {isEditing ? (
                                                    <>
                                                        <ActionBtn
                                                            $variant="save"
                                                            onClick={() => saveRow(row)}
                                                        >
                                                            <i className="fas fa-check" />
                                                        </ActionBtn>

                                                        <ActionBtn
                                                            $variant="cancel"
                                                            onClick={() => cancelEdit(row)}
                                                        >
                                                            <i className="fas fa-times" />
                                                        </ActionBtn>
                                                    </>
                                                ) : (
                                                    <>
                                                        {onView && (
                                                            <ActionBtn
                                                                $variant="view"
                                                                onClick={() => onView(row)}
                                                            >
                                                                <i className="fas fa-eye" />
                                                            </ActionBtn>
                                                        )}

                                                        {onDelete && (
                                                            <ActionBtn
                                                                $variant="del"
                                                                onClick={() => handleDelete(row)}
                                                            >
                                                                <i className="fas fa-trash" />
                                                            </ActionBtn>
                                                        )}

                                                        {extraActions?.(row)?.map((action, i) => (
                                                            <ActionBtn
                                                                key={i}
                                                                $variant={action.variant}
                                                                onClick={action.onClick}
                                                                title={action.label}
                                                            >
                                                                <i className={action.icon} />
                                                            </ActionBtn>
                                                        ))}
                                                    </>
                                                )}
                                            </ActionsCell>
                                        </Td>
                                    )}
                                </Tr>
                            );
                        })}
                    </tbody>
                </StyledTable>

                {pageRows.length === 0 && (
                    <EmptyState>
                        <i className="fas fa-table" />
                        {emptyMessage}
                    </EmptyState>
                )}
            </TableWrap>

            {showPagination && (
                <Footer>
                    <span>
                        Showing{" "}
                        {
                            pageRows.length === 0
                                ? 0
                                : ((activePage - 1) * pageSize) + 1
                        }
                        –
                        {
                            Math.min(
                                activePage * pageSize,
                                serverSide
                                    ? totalRecords
                                    : processedRows.length
                            )
                        }
                        {" "}of{" "}
                        {
                            serverSide
                                ? totalRecords
                                : processedRows.length
                        }
                        {" "}records
                    </span>

                    <PgBtns>
                        <PgBtn
                            onClick={() => {
                                if (activePage <= 1) return;

                                if (serverSide) {
                                    onPageChange?.(activePage - 1);
                                } else {
                                    setPage(p => Math.max(1, p - 1));
                                }
                            }}
                        >
                            <i className="fas fa-chevron-left" />
                        </PgBtn>

                        {renderPaginationButtons()}

                        <PgBtn
                            onClick={() => {
                                if (activePage >= totalPages) return;

                                if (serverSide) {
                                    onPageChange?.(activePage + 1);
                                } else {
                                    setPage(p =>
                                        Math.min(totalPages, p + 1)
                                    );
                                }
                            }}
                        >
                            <i className="fas fa-chevron-right" />
                        </PgBtn>
                    </PgBtns>
                </Footer>
            )}
        </Shell>
    );
};

export default DataTable;